import {
  type DynamicClient,
  NetworkNotAddedError,
  NetworkSwitchingUnavailableError,
  UnrecognizedNetworkError,
  UserRejectedError,
  getNetworksData,
} from '@dynamic-labs-sdk/client';
import type { ExtendParameters } from '@dynamic-labs-sdk/client/core';
import { toHex } from 'viem';

import type {
  EIP1193Provider,
  EvmWalletProvider,
} from '../../../EvmWalletProvider.types';
import { isUnrecognizedChainError } from '../isUnrecognizedChainError';

type switchActiveNetwork = ExtendParameters<
  EvmWalletProvider['switchActiveNetwork'],
  {
    client: DynamicClient;
    eip1193: EIP1193Provider;
    walletProviderKey: string;
  }
>;

/** @not-instrumented */
export const switchActiveNetwork: switchActiveNetwork = async ({
  networkId,
  client,
  eip1193,
  walletProviderKey,
}) => {
  const chainId = toHex(parseInt(networkId));

  try {
    await eip1193.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId }],
    });
  } catch (error) {
    // We declare this error here to DRY it from being created in 2 different points below
    const userRejectedError = new UserRejectedError({
      action: 'switch wallet active network',
    });

    if (typeof error === 'string' && error.includes('rejected')) {
      throw userRejectedError;
    }

    if (typeof error !== 'object' || error === null) {
      throw error;
    }

    if (
      'details' in error &&
      Array.isArray(error.details) &&
      error.details.includes(
        // eslint-disable-next-line custom-rules/ban-ethereum-eth-terms
        '"wallet_switchEthereumChain" not implemented'
      )
    ) {
      throw new NetworkSwitchingUnavailableError({
        originalError: error,
        walletProviderKey,
      });
    }

    // Bear in mind: the unrecognized chain error also contains 'rejected' in message,
    // so it needs to be checked for before the rejection error itself.
    if (isUnrecognizedChainError(error)) {
      const networkData = getNetworksData(client).find(
        (networkData) =>
          networkData.networkId === networkId && networkData.chain === 'EVM'
      );

      if (!networkData) {
        throw new UnrecognizedNetworkError({
          networkId,
          originalError: error,
          walletProviderKey,
        });
      }

      throw new NetworkNotAddedError({
        networkData,
        networkId,
        originalError: error,
        walletProviderKey,
      });
    } else if (
      'message' in error &&
      typeof error.message === 'string' &&
      error.message.includes('rejected')
    ) {
      throw userRejectedError;
    } else {
      throw error;
    }
  }
};
